import { X, Mail, Phone, MapPin, Calendar, Shield, FileText, CreditCard, User as UserIcon, Copy } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";

/**
 * Admin-only detail modal exposing every contact / NDIS / emergency / plan-manager field
 * for a user. Read-only — just for support / onboarding lookups.
 */
export default function UserDetailModal({ open, user, onClose }) {
  if (!open || !user) return null;

  const copy = (v, label) => {
    if (!v) return;
    try {
      navigator.clipboard.writeText(String(v));
      toast.success(`${label} copied`);
    } catch (_) {}
  };

  const Row = ({ icon: Icon, label, value, copyable }) => (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="w-3.5 h-3.5 text-slate-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">{label}</p>
        <p className="text-sm text-slate-800 break-words mt-0.5">{value || <span className="text-slate-400">—</span>}</p>
      </div>
      {copyable && value && (
        <button
          onClick={() => copy(value, label)}
          className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 shrink-0"
          aria-label={`Copy ${label}`}
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );

  const planLabel = (user.ndis_plan_type || "").replace(/_/g, " ");
  const typeLabel = (user.user_type || "user").replace(/_/g, " ");

  return (
    <div
      className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={onClose}
      data-testid="user-detail-modal"
    >
      <div
        className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-slate-100 bg-slate-50/60">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-[#87CEEB]/20 flex items-center justify-center shrink-0">
              <span className="text-[#5BA3C0] font-semibold">{user.name?.charAt(0) || "?"}</span>
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-slate-900 truncate">{user.name || "—"}</h3>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <Badge className="bg-[#87CEEB]/15 text-[#5BA3C0] capitalize">{typeLabel}</Badge>
                {user.email_verified && <Badge className="bg-emerald-100 text-emerald-700">Email verified</Badge>}
                {user.profile_completed && <Badge className="bg-slate-200 text-slate-700">Onboarded</Badge>}
                {user.service_agreement_approved && <Badge className="bg-emerald-100 text-emerald-700">ESA approved</Badge>}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 shrink-0"
            aria-label="Close"
            data-testid="user-detail-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-3">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mt-2 mb-1">Contact</p>
          <Row icon={Mail} label="Email" value={user.email} copyable />
          <Row icon={Phone} label="Phone" value={user.phone} copyable />
          <Row icon={MapPin} label="Location" value={[user.location, user.state].filter(Boolean).join(", ")} />
          <Row icon={Calendar} label="Date of birth" value={user.dob} />

          {user.user_type === "participant" && (
            <>
              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mt-4 mb-1">NDIS</p>
              <Row icon={CreditCard} label="NDIS number" value={user.ndis_number} copyable />
              <Row icon={FileText} label="Plan management" value={planLabel} />
              <Row icon={UserIcon} label="Plan manager — name" value={user.plan_manager_name} />
              <Row icon={Mail} label="Plan manager — email" value={user.plan_manager_email} copyable />
              <Row icon={Shield} label="Support needs" value={(user.support_needs || []).join(", ")} />

              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mt-4 mb-1">Emergency contact</p>
              <Row icon={UserIcon} label="Name" value={user.emergency_contact_name} />
              <Row icon={Phone} label="Phone" value={user.emergency_contact_phone} copyable />
            </>
          )}

          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mt-4 mb-1">Account</p>
          <Row icon={UserIcon} label="User ID" value={user.user_id} copyable />
          <Row icon={Calendar} label="Signed up" value={user.created_at ? new Date(user.created_at).toLocaleString() : "—"} />
          {user.service_agreement_requested_at && (
            <Row icon={FileText} label="ESA requested" value={new Date(user.service_agreement_requested_at).toLocaleString()} />
          )}
          {user.service_agreement_approved_at && (
            <Row icon={FileText} label="ESA approved" value={new Date(user.service_agreement_approved_at).toLocaleString()} />
          )}
        </div>

        <div className="border-t border-slate-100 px-5 py-3 flex justify-end gap-2 bg-slate-50/60">
          {user.email && (
            <Button variant="outline" size="sm" asChild>
              <a href={`mailto:${user.email}`} data-testid="user-detail-email-link">Email {user.name?.split(" ")[0] || "user"}</a>
            </Button>
          )}
          {user.phone && (
            <Button variant="outline" size="sm" asChild>
              <a href={`tel:${user.phone}`} data-testid="user-detail-call-link">Call</a>
            </Button>
          )}
          <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  );
}
